import { FormControl, Grid, InputLabel, MenuItem, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { useEffect } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { useTalent } from '../../../../../../Hooks/useTalent';
import { useAppDispatch, useAppSelector } from '../../../../../../Redux/store';
import { addEducation, setNextDisabled } from '../../../Slices/TalentProfileCreationSlice';
import { Education } from '../../../../../../Types/Resume';
import { EducationSchema } from '../../../../../../Schemas/ResumeSchemas';
import * as yup from "yup";
import { useMobileView } from '../../../../../../Hooks/useMobileView';

interface IProps {
    educationForm: UseFormReturn<yup.InferType<typeof EducationSchema>>
}
const EducationForm = ({ educationForm }: IProps) => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const { talent } = useTalent();
    const {
        control,
        formState: { isValid }
    } = educationForm;
    /* ******************** Variables ******************** */
    const { flexDirection } = useMobileView();
    /* ******************** Functions ******************** */
    /* ******************** Effects ******************** */
    useEffect(() => {
        isValid ? dispatch(setNextDisabled(false)) : dispatch(setNextDisabled(true));
    }, [isValid])
    /* ******************** JSX ******************** */
    return (
        <Grid container gap={'20px'} display={"flex"} flexDirection={"column"}>
            <Typography variant='lora-title-large' color={'primary'} fontWeight={'bold'}>Add your education</Typography>
            <Grid item sx={{overflowY: "auto"}}  maxHeight={"550px"} gap={'20px'} display={"flex"} flexDirection={"column"} paddingRight={4}>
                <Grid item display={"flex"} flexDirection={flexDirection} gap={"12px"} >
                    <Grid item xs display={"flex"} flexDirection={"column"}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>School</Typography>  
                        <Controller
                            name="school"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>(                 
                                <TextField
                                    {...field}
                                    fullWidth
                                    // error={hasErrors(field.name, errors)}
                                    placeholder='Name of school'
                                    sx={{
                                        width: "100%"
                                    }}
                                />
                            )}
                        />   
                    </Grid>
                    {/* <Grid item display={"flex"} flex={1} flexDirection={"row"} alignItems={'center'} justifyContent={'flex-end'} gap={1}>
                        <Box>
                            <IconButton onClick={handleSave}>
                                <Save />
                            </IconButton>
                        </Box>
                        <Box>
                            <IconButton onClick={handleDelete}>
                                <Delete color='error'/>
                            </IconButton>
                        </Box>
                    </Grid> */}
                </Grid>
                <Grid item display={"flex"} flexDirection={flexDirection} gap={"12px"}>
                    <Grid item display={"flex"} flexDirection={"column"}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>Degree</Typography>  
                        <Controller
                            name="title"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <FormControl>   
                                    <InputLabel sx={{opacity: 1}}>{field.value ?"":"Degree"}</InputLabel>                   
                                    <TextField
                                        {...field}
                                        select
                                        // error={hasErrors(field.name, errors)}
                                        sx={{
                                            width: "270px"
                                        }}
                                    >{
                                        [
                                            "High School", 
                                            "Associates", 
                                            "Bachelors",
                                            "Masters",
                                            "PhD"
                                        ].map((language) => (
                                            <MenuItem key={language} value={language}>
                                                {language}
                                            </MenuItem>
                                        ))
                                    }
                                    </TextField >
                                </FormControl>
                            )}
                        />   
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>Major</Typography>  
                        <Controller
                            name="major"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <FormControl>   
                                    <InputLabel sx={{opacity: 1}}>{field.value ?"":"Major"}</InputLabel>                   
                                    <TextField
                                        {...field}
                                        // error={hasErrors(field.name, errors)}
                                        // select
                                        sx={{
                                            width: "270px"
                                        }}
                                    />
                                        {/* {
                                            [
                                                "English", 
                                                "French", 
                                                "Swahili"
                                            ].map((language) => (
                                                <MenuItem key={language} value={language}>
                                                    {language}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField> */}
                                </FormControl>
                            )}
                        />   
                    </Grid>
                </Grid>
                <Grid item display={"flex"} flexDirection={flexDirection} gap={"12px"}>
                    <Grid item display={"flex"} flexDirection={"column"} flex={1}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>Start</Typography>  
                        <Controller
                            name="start_date"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>(      
                                <DatePicker
                                    {...field}
                                    disableFuture
                                    slotProps={{
                                        textField: {
                                            placeholder: 'Start month and year'
                                        },
                                        field: {
                                            clearable: true
                                        }
                                    }}
                                    value={field?.value ? moment(field.value).local() : undefined} 
                                    format='MM YYYY'
                                    views={['month', 'year']}                             
                                />
                            )}
                        />   
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} flex={1}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>End</Typography>  
                        <Controller
                            name="end_date"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>(
                                <DatePicker
                                    {...field}
                                    disableFuture
                                    slotProps={{
                                        textField: {
                                            placeholder: 'End month and year'
                                        },
                                        field: {
                                            clearable: true
                                        }
                                    }}
                                    value={field?.value ? moment(field.value).local() : undefined} 
                                    format='MM YYYY'
                                    views={['month', 'year']}                              
                                />
                            )}
                        />   
                    </Grid>
                </Grid>
                <Typography variant='body-small' color={'black.200'} >*You can update this information anytime. For now, please add your most recent details to complete your profile.</Typography>
            </Grid>
        </Grid>
    )
}

export default EducationForm